import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { session } from './session';
import './design/tokens.css';
import './design/app.css';
import './design/zeroqube.css';

void session.start();

const el = document.getElementById('root');
if (!el) throw new Error('missing #root element');

createRoot(el).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// DEV-only test hook (app/e2e/smoke.spec.ts: "the worker answers wipe").
// Constructs the same worker module the app's signing flow uses
// (docs/08-app-design.md Section 7, worker-entry.ts) and round-trips its
// 'wipe' request, without wiring it into any real signing state.
// `import.meta.env.DEV` is a build-time constant Vite compiles away (false,
// then dead-code-eliminated) for `vite build` / `npm run build`, so this
// never reaches a production bundle.
if (import.meta.env.DEV) {
  let worker: Worker | undefined;
  let nextRequestId = 1;
  (
    window as unknown as {
      __thevault_test: {
        workerWipe: () => Promise<string>;
        /** The build identity Verify reads (R51), for the evidence spec: source commit plus whether the working tree was clean. */
        build: () => { commit: string; dirty: boolean };
        /**
         * The legacy escape hatch's operation (docs/18 2.4 and section 10; e2e legacy-escape.spec.ts): its phase,
         * whether it holds a signer, wire, envelope or seed and how many times a signer was installed
         * (`signerInstalls`), never their contents; null when the session's slot is empty.
         */
        legacySendOut: () => ReturnType<typeof session.legacySendOut>;
      };
    }
  ).__thevault_test = {
    workerWipe: () =>
      new Promise<string>((resolve, reject) => {
        worker ??= new Worker(new URL('./worker-entry.ts', import.meta.url), { type: 'module' });
        const id = nextRequestId++;
        const timer = setTimeout(() => reject(new Error('worker did not respond to wipe within 5s')), 5000);
        const onMessage = (ev: MessageEvent<{ id: number; type: string }>) => {
          if (ev.data.id !== id) return;
          worker?.removeEventListener('message', onMessage);
          clearTimeout(timer);
          resolve(ev.data.type);
        };
        worker.addEventListener('message', onMessage);
        worker.postMessage({ id, type: 'wipe' });
      }),
    build: () => ({ commit: __GIT_COMMIT__, dirty: __GIT_DIRTY__ }),
    legacySendOut: () => session.legacySendOut(),
  };
}
